'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { emergencyFundService } from '@/lib/services/emergencyFundService';

interface EmergencyProject {
  id: number;
  title: string;
  location: string;
  crisis: string;
  urgency: 'critical' | 'urgent' | 'active';
  targetAmount: number;
  raisedAmount: number;
  beneficiaries: number;
  timeRemaining: string;
  image: string;
  alt: string;
  description: string;
}

interface EmergencyFundModalProps {
  isOpen: boolean;
  onClose: () => void;
  project: EmergencyProject | null;
}

const EmergencyFundModal: React.FC<EmergencyFundModalProps> = ({ isOpen, onClose, project }) => {
  const [formData, setFormData] = useState({
    donorName: '',
    donorEmail: '',
    donorPhone: '',
    contributionAmount: '',
    paymentPlatform: 'PayPal',
    message: '',
    isAnonymous: false
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState('');

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.donorName.trim()) {
      newErrors.donorName = 'Name is required';
    }

    if (!formData.donorEmail.trim()) {
      newErrors.donorEmail = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.donorEmail)) {
      newErrors.donorEmail = 'Invalid email format';
    }

    if (!formData.contributionAmount) {
      newErrors.contributionAmount = 'Contribution amount is required';
    } else if (Number(formData.contributionAmount) <= 0) {
      newErrors.contributionAmount = 'Amount must be greater than 0';
    }

    if (!formData.paymentPlatform) {
      newErrors.paymentPlatform = 'Payment platform is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !project) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const result = await emergencyFundService.submitContribution({
        projectId: project.id,
        projectTitle: project.title,
        donorName: formData.donorName,
        donorEmail: formData.donorEmail,
        donorPhone: formData.donorPhone || undefined,
        contributionAmount: Number(formData.contributionAmount),
        paymentPlatform: formData.paymentPlatform,
        message: formData.message || undefined,
        isAnonymous: formData.isAnonymous
      });

      if (result.success) {
        setSubmitStatus('success');
        setSubmitMessage('Thank you for your contribution! Your support will make a real difference.');
        
        // Reset form
        setFormData({
          donorName: '',
          donorEmail: '',
          donorPhone: '',
          contributionAmount: '',
          paymentPlatform: 'PayPal',
          message: '',
          isAnonymous: false
        });

        // Close modal after 2 seconds
        setTimeout(() => {
          onClose();
          setSubmitStatus('idle');
          setSubmitMessage('');
        }, 2000);
      } else {
        setSubmitStatus('error');
        setSubmitMessage(result.error || 'Failed to submit contribution. Please try again.');
      }
    } catch (error: any) {
      setSubmitStatus('error');
      setSubmitMessage('An unexpected error occurred. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  if (!isOpen || !project) return null;

  return (
    <div className="fixed inset-0 bg-foreground/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-headline font-bold text-foreground mb-1">
              Emergency Contribution
            </h2>
            <p className="text-sm text-muted-foreground font-body">
              {project.title}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Close modal"
          >
            <Icon name="XMarkIcon" size={24} variant="outline" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {submitStatus === 'success' && (
            <div className="bg-success/10 border border-success text-success px-4 py-3 rounded-lg flex items-center space-x-3">
              <Icon name="CheckCircleIcon" size={20} variant="solid" />
              <span className="text-sm font-body">{submitMessage}</span>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="bg-error/10 border border-error text-error px-4 py-3 rounded-lg flex items-center space-x-3">
              <Icon name="ExclamationCircleIcon" size={20} variant="solid" />
              <span className="text-sm font-body">{submitMessage}</span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="donorName" className="block text-sm font-cta font-bold text-foreground mb-2">
                Full Name *
              </label>
              <input
                type="text"
                id="donorName"
                name="donorName"
                value={formData.donorName}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background text-foreground"
                placeholder="John Doe"
              />
              {errors.donorName && (
                <p className="text-error text-xs mt-1 font-body">{errors.donorName}</p>
              )}
            </div>

            <div>
              <label htmlFor="donorEmail" className="block text-sm font-cta font-bold text-foreground mb-2">
                Email Address *
              </label>
              <input
                type="email"
                id="donorEmail"
                name="donorEmail"
                value={formData.donorEmail}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background text-foreground"
                placeholder="john@example.com"
              />
              {errors.donorEmail && (
                <p className="text-error text-xs mt-1 font-body">{errors.donorEmail}</p>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="donorPhone" className="block text-sm font-cta font-bold text-foreground mb-2">
              Phone Number (Optional)
            </label>
            <input
              type="tel"
              id="donorPhone"
              name="donorPhone"
              value={formData.donorPhone}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background text-foreground"
              placeholder="+1234567890"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="contributionAmount" className="block text-sm font-cta font-bold text-foreground mb-2">
                Contribution Amount (USD) *
              </label>
              <input
                type="number"
                id="contributionAmount"
                name="contributionAmount"
                value={formData.contributionAmount}
                onChange={handleChange}
                min="1"
                step="0.01"
                className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background text-foreground"
                placeholder="100.00"
              />
              {errors.contributionAmount && (
                <p className="text-error text-xs mt-1 font-body">{errors.contributionAmount}</p>
              )}
            </div>

            <div>
              <label htmlFor="paymentPlatform" className="block text-sm font-cta font-bold text-foreground mb-2">
                Payment Platform *
              </label>
              <select
                id="paymentPlatform"
                name="paymentPlatform"
                value={formData.paymentPlatform}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background text-foreground"
              >
                <option value="PayPal">PayPal</option>
                <option value="Flutterwave">Flutterwave</option>
              </select>
              {errors.paymentPlatform && (
                <p className="text-error text-xs mt-1 font-body">{errors.paymentPlatform}</p>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="message" className="block text-sm font-cta font-bold text-foreground mb-2">
              Message of Support (Optional)
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={4}
              className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background text-foreground resize-none"
              placeholder="Share a message of hope and support..."
            />
          </div>

          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="isAnonymous"
              name="isAnonymous"
              checked={formData.isAnonymous}
              onChange={handleChange}
              className="w-4 h-4 text-primary border-border rounded focus:ring-2 focus:ring-primary"
            />
            <label htmlFor="isAnonymous" className="text-sm text-muted-foreground font-body">
              Make my contribution anonymous
            </label>
          </div>

          <div className="flex items-center justify-end space-x-4 pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 text-muted-foreground hover:text-foreground font-cta font-bold transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-3 bg-error text-error-foreground rounded-lg font-cta font-bold transition-all duration-300 hover:scale-105 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <Icon name="ArrowPathIcon" size={18} className="animate-spin" variant="outline" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <Icon name="HeartIcon" size={18} variant="solid" />
                  <span>Contribute Now</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmergencyFundModal;